import React, { useState } from 'react';
import { applyAccentColor, ACCENT_PRESETS } from '@/shared/utils/accentColor';

interface AccentColorPickerProps {
  onColorChange?: (h: number, s: number, l: number) => void;
  initialHue?: number;
  initialSaturation?: number;
  initialLightness?: number;
}

export const AccentColorPicker: React.FC<AccentColorPickerProps> = ({
  onColorChange,
  initialHue = 210,
  initialSaturation = 79,
  initialLightness = 46,
}) => {
  const [hue, setHue] = useState(initialHue);
  const [saturation, setSaturation] = useState(initialSaturation);
  const [lightness, setLightness] = useState(initialLightness);

  const handleChange = (h: number, s: number, l: number) => {
    setHue(h);
    setSaturation(s);
    setLightness(l);
    applyAccentColor(h, s, l);
    onColorChange?.(h, s, l);
  };

  return (
    <div className="space-y-4">
      {/* Preset swatches */}
      <div className="grid grid-cols-6 gap-2 mb-4">
        {ACCENT_PRESETS.map((preset) => (
          <button
            key={preset.name}
            onClick={() => handleChange(preset.h, preset.s, preset.l)}
            className="h-10 rounded-medium transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-400"
            style={{ backgroundColor: `hsl(${preset.h}, ${preset.s}%, ${preset.l}%)` }}
            title={preset.name}
            aria-label={`Select ${preset.name} accent color`}
          />
        ))}
      </div>

      {/* HSL Sliders */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-dark-text block">
          Hue: {hue}°
        </label>
        <input
          type="range"
          min="0"
          max="360"
          value={hue}
          onChange={(e) => handleChange(Number(e.target.value), saturation, lightness)}
          className="w-full h-2 rounded-full appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right,
              hsl(0, ${saturation}%, ${lightness}%),
              hsl(60, ${saturation}%, ${lightness}%),
              hsl(120, ${saturation}%, ${lightness}%),
              hsl(180, ${saturation}%, ${lightness}%),
              hsl(240, ${saturation}%, ${lightness}%),
              hsl(300, ${saturation}%, ${lightness}%),
              hsl(360, ${saturation}%, ${lightness}%))`,
          }}
          aria-label="Hue slider"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-dark-text block">
          Saturation: {saturation}%
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={saturation}
          onChange={(e) => handleChange(hue, Number(e.target.value), lightness)}
          className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gray-300"
          style={{
            background: `linear-gradient(to right,
              hsl(${hue}, 0%, ${lightness}%),
              hsl(${hue}, 100%, ${lightness}%))`,
          }}
          aria-label="Saturation slider"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-dark-text block">
          Lightness: {lightness}%
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={lightness}
          onChange={(e) => handleChange(hue, saturation, Number(e.target.value))}
          className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gray-300"
          style={{
            background: `linear-gradient(to right,
              hsl(${hue}, ${saturation}%, 0%),
              hsl(${hue}, ${saturation}%, 50%),
              hsl(${hue}, ${saturation}%, 100%))`,
          }}
          aria-label="Lightness slider"
        />
      </div>

      {/* Color preview */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-dark-text block">
          Preview
        </label>
        <div
          className="h-16 rounded-medium border-2 border-dark-border/30"
          style={{ backgroundColor: `hsl(${hue}, ${saturation}%, ${lightness}%)` }}
          aria-label="Color preview"
        />
      </div>
    </div>
  );
};
